import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { ClearIcon, UndoIcon } from "./Icons";
import ui from "./ui.module.css";
import { DRAWING_TYPE } from "../data/learnerService";

// The one ink surface: drawing and tracing steps, and the optional working
// space beside a numeric answer. Strokes are bounded runtime state (stored as
// `strokes-v1` by whoever owns the step); a flattened picture of meaningful
// ink becomes protected drawing evidence through the existing lifecycle.

const COLOURS = [
  { value: "#262626", name: "Black" },
  { value: "#e04848", name: "Red" },
  { value: "#2f7de1", name: "Blue" },
  { value: "#43b061", name: "Green" },
];
const LINE_WIDTH = 8;
// Tracing follows a template: one clear colour and a line thick enough to
// cover the guide comfortably.
const TRACE_COLOUR = "#2f7de1";
const TRACE_WIDTH = 14;
const EXPORT_WIDTH = 1200;
// Matches the template's CSS padding, so the page drawn here is exactly the
// page shown on screen.
const PAGE_PADDING = 16;

const loaded = (image) => Boolean(image?.naturalWidth && image?.naturalHeight);

// Where the page sits inside a space: a background or template is shown
// contained (letterboxed) within the padding; without one the page is the
// whole space. Points are stored relative to this page, so ink stays on the
// worksheet line it was written on when the space changes shape (a tablet
// turned round, a resumed attempt on another screen).
export const pageIn = (width, height, image, padding) => {
  if (!loaded(image)) return { x: 0, y: 0, width, height };
  const scale = Math.min(
    (width - padding * 2) / image.naturalWidth,
    (height - padding * 2) / image.naturalHeight
  );
  const pageWidth = Math.max(1, image.naturalWidth * scale);
  const pageHeight = Math.max(1, image.naturalHeight * scale);
  return {
    x: (width - pageWidth) / 2,
    y: (height - pageHeight) / 2,
    width: pageWidth,
    height: pageHeight,
  };
};

const paint = (ctx, strokes, page, widthScale = 1) => {
  const at = ([x, y]) => [page.x + x * page.width, page.y + y * page.height];
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  strokes.forEach(({ colour, width = LINE_WIDTH, points }) => {
    ctx.strokeStyle = colour;
    ctx.fillStyle = colour;
    ctx.lineWidth = width * widthScale;
    if (points.length === 1) {
      ctx.beginPath();
      ctx.arc(...at(points[0]), (width * widthScale) / 2, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    ctx.beginPath();
    points.forEach((point, i) =>
      i ? ctx.lineTo(...at(point)) : ctx.moveTo(...at(point))
    );
    ctx.stroke();
  });
};

// Evidence is the page the teacher needs to review: approved background or
// tracing template plus the learner's ink. The source asset remains protected
// curriculum content; this flattened PNG is protected learner evidence.
const exportPng = (strokes, size, background) =>
  new Promise((resolve, reject) => {
    const widthScale = EXPORT_WIDTH / size.width;
    const canvas = document.createElement("canvas");
    canvas.width = EXPORT_WIDTH;
    canvas.height = Math.round(size.height * widthScale);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const page = pageIn(
      canvas.width,
      canvas.height,
      background,
      PAGE_PADDING * widthScale
    );
    if (loaded(background)) {
      ctx.drawImage(background, page.x, page.y, page.width, page.height);
    }
    paint(ctx, strokes, page, widthScale);
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("export failed"))),
      DRAWING_TYPE
    );
  });

// Strokes saved by an earlier visit, or none. Anything malformed is dropped
// rather than drawn.
export const savedStrokesOf = (work) => {
  const strokes = work?.format === "strokes-v1" ? work.strokes : null;
  if (!Array.isArray(strokes)) return [];
  return strokes.filter(
    (stroke) =>
      typeof stroke?.colour === "string" &&
      Number.isFinite(stroke?.width) &&
      Array.isArray(stroke?.points) &&
      stroke.points.length > 0 &&
      // Page-relative: ink in the margin beside a page falls outside 0–1.
      stroke.points.every(
        (point) =>
          Array.isArray(point) &&
          point.length === 2 &&
          point.every(
            (value) => Number.isFinite(value) && Math.abs(value) <= 10
          )
      )
  );
};

// Mouse, touch and passive capacitive stylus all arrive as pointer events.
// Undo and Clear are always available. `actions(ready)` adds the owner's own
// buttons (Done) beside them.
//
// `tracing` shows the template beneath the learner's lines, cannot start until
// it has loaded, and hands over to `onUnavailable` if it cannot load.
const InkPad = forwardRef(
  (
    {
      template = null,
      saved = null,
      onChange,
      onUnavailable,
      disabled = false,
      tracing = false,
      label,
      className,
      actions,
    },
    ref
  ) => {
    // Ink is placed relative to the page, so it waits until the page is known.
    // A worksheet background that fails to load leaves a plain drawing space;
    // a tracing template that fails hands over to `onUnavailable`.
    const [ready, setReady] = useState(!template);
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    const backgroundRef = useRef(null);
    const sizeRef = useRef({ width: 1, height: 1 });
    const activeRef = useRef(null);
    const [strokes, setStrokes] = useState(() => saved || []);
    const [cleared, setCleared] = useState(null);
    const [colour, setColour] = useState(COLOURS[0].value);
    // The exported picture is kept for the page session so a retry re-sends the
    // same drawing rather than a new one.
    const exported = useRef({ strokes: null, blob: null });

    const redraw = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ratio = window.devicePixelRatio || 1;
      const ctx = canvas.getContext("2d");
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      const { width, height } = sizeRef.current;
      paint(
        ctx,
        activeRef.current ? [...strokes, activeRef.current] : strokes,
        pageIn(width, height, backgroundRef.current, PAGE_PADDING)
      );
      // `ready` changes when the page loads, which is when the page is known.
    }, [strokes, ready]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      const wrap = wrapRef.current;
      const resize = () => {
        // The observer can still fire while the step is being taken down.
        if (!canvasRef.current) return;
        const { width, height } = wrap.getBoundingClientRect();
        const ratio = window.devicePixelRatio || 1;
        sizeRef.current = { width, height };
        canvasRef.current.width = Math.round(width * ratio);
        canvasRef.current.height = Math.round(height * ratio);
        redraw();
      };
      resize();
      const observer = new ResizeObserver(resize);
      observer.observe(wrap);
      return () => observer.disconnect();
    }, [redraw]);

    const pointOf = (event) => {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, event.clientY - rect.top));
      const page = pageIn(
        rect.width,
        rect.height,
        backgroundRef.current,
        PAGE_PADDING
      );
      return [(x - page.x) / page.width, (y - page.y) / page.height];
    };

    const persist = (next) => onChange?.(next);

    const down = (event) => {
      if (
        !ready ||
        disabled ||
        (event.pointerType === "mouse" && event.button !== 0)
      )
        return;
      event.preventDefault();
      canvasRef.current.setPointerCapture(event.pointerId);
      activeRef.current = {
        colour: tracing ? TRACE_COLOUR : colour,
        width: tracing ? TRACE_WIDTH : LINE_WIDTH,
        points: [pointOf(event)],
        id: event.pointerId,
      };
      redraw();
    };

    const move = (event) => {
      const active = activeRef.current;
      if (!active || active.id !== event.pointerId) return;
      const events = event.nativeEvent.getCoalescedEvents?.() || [event];
      events.forEach((e) => active.points.push(pointOf(e)));
      redraw();
    };

    const up = (event) => {
      const active = activeRef.current;
      if (!active || active.id !== event.pointerId) return;
      activeRef.current = null;
      const next = [
        ...strokes,
        { colour: active.colour, width: active.width, points: active.points },
      ];
      setStrokes(next);
      persist(next);
      setCleared(null);
    };

    const undo = () => {
      if (strokes.length) {
        const next = strokes.slice(0, -1);
        setStrokes(next);
        persist(next);
      } else if (cleared) {
        setStrokes(cleared);
        persist(cleared);
        setCleared(null);
      }
    };

    const clear = () => {
      if (!strokes.length) return;
      setCleared(strokes);
      setStrokes([]);
      persist([]);
    };

    // The exported picture is kept for the page session so a retry re-sends
    // the same picture rather than a new one. No ink, no picture.
    useImperativeHandle(
      ref,
      () => ({
        ready,
        strokes,
        exportPng: async () => {
          if (!strokes.length) return null;
          if (exported.current.strokes !== strokes) {
            exported.current = {
              strokes,
              blob: await exportPng(
                strokes,
                sizeRef.current,
                backgroundRef.current
              ),
            };
          }
          return exported.current.blob;
        },
      }),
      [ready, strokes]
    );

    return (
      <div className={`${ui.drawing} ${className || ""}`}>
        <div className={ui.canvasWrap} ref={wrapRef} aria-busy={!ready}>
          {template && (
            <img
              ref={backgroundRef}
              className={ui.template}
              src={template.src}
              alt={template.alt}
              // The gateway authorises media by cookie, so the request must
              // carry credentials; CORS then lets the page be exported.
              crossOrigin="use-credentials"
              draggable={false}
              onLoad={() => setReady(true)}
              onError={() => (tracing ? onUnavailable?.() : setReady(true))}
            />
          )}
          <canvas
            ref={canvasRef}
            className={ui.canvas}
            onPointerDown={down}
            onPointerMove={move}
            onPointerUp={up}
            onPointerCancel={up}
            aria-label={label || (tracing ? "Tracing space" : "Drawing space")}
            role="img"
          />
        </div>
        <div className={ui.tools}>
          {!tracing && (
            <div className={ui.swatches} role="radiogroup" aria-label="Colour">
              {COLOURS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  role="radio"
                  aria-checked={colour === c.value}
                  aria-label={c.name}
                  className={`${ui.swatch} ${colour === c.value ? ui.swatchOn : ""}`}
                  style={{ background: c.value }}
                  onClick={() => setColour(c.value)}
                />
              ))}
            </div>
          )}
          <div className={ui.toolButtons}>
            <button
              type="button"
              className={`${ui.button} ${ui.large} ${ui.secondary}`}
              onClick={undo}
              disabled={!ready || disabled}
            >
              <UndoIcon size={24} />
              Undo
            </button>
            <button
              type="button"
              className={`${ui.button} ${ui.large} ${ui.secondary}`}
              onClick={clear}
              disabled={!ready || disabled}
            >
              <ClearIcon size={24} />
              Clear
            </button>
            {actions?.(ready)}
          </div>
        </div>
      </div>
    );
  }
);

export default InkPad;
